// src/context/ErrorContext.js
import React, { createContext, useContext, useReducer, useCallback } from 'react';
import PropTypes from 'prop-types';
import { logger } from '../utils/logger';
import { useHealth } from './HealthContext'; // We'll use this via hook

const ErrorContext = createContext();

// Error types for categorization
export const ErrorTypes = {
  NETWORK: 'network',
  CREDITS: 'credits', 
  AUTH: 'auth',
  VALIDATION: 'validation',
  SERVER: 'server',
  RATE_LIMIT: 'rate_limit',
  BACKEND_DOWN: 'backend_down',
  AI_UNAVAILABLE: 'ai_unavailable',
  UNKNOWN: 'unknown'
};

const errorReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_ERROR':
      const newError = {
        ...action.payload,
        id: Date.now(),
        timestamp: Date.now()
      };
      
      // Log the error
      logger.error(newError.cid || 'error-context', `[ErrorContext] ${newError.type} error`, {
        message: newError.message,
        component: newError.component,
        health_status: action.payload.healthStatus // Include health context
      });
      
      return {
        ...state,
        errors: [newError, ...state.errors].slice(0, 10) // Keep last 10
      };
      
    case 'REMOVE_ERROR':
      return {
        ...state,
        errors: state.errors.filter(error => error.id !== action.payload.id)
      };
      
    case 'CLEAR_ERRORS':
      return {
        ...state,
        errors: []
      };
      
    case 'CLEAR_TYPE':
      return {
        ...state,
        errors: state.errors.filter(error => error.type !== action.payload.errorType)
      };
      
    default:
      return state;
  }
};

// Enhanced error categorizer that considers health status
const categorizeError = (error, healthStatus = null) => {
  // Network errors
  if (error.message?.includes('Network') || 
      error.message?.includes('Failed to fetch') ||
      error.message?.includes('timeout') ||
      error.code === 'NETWORK_ERROR') {
    
    // Check if health monitor already knows backend is down
    if (healthStatus && !healthStatus.backend.healthy) {
      return ErrorTypes.BACKEND_DOWN;
    }
    return ErrorTypes.NETWORK;
  }
  
  // Credit errors (DeepSeek specific)
  if (error.message?.includes('credit') ||
      error.message?.includes('insufficient balance') ||
      error.message?.includes('platform.deepseek.com') ||
      error.response?.status === 402) {
    return ErrorTypes.CREDITS;
  }
  
  // Rate limiting
  if (error.response?.status === 429) {
    return ErrorTypes.RATE_LIMIT;
  }
  
  // Auth errors
  if (error.response?.status === 401 || 
      error.response?.status === 403 ||
      error.message?.includes('auth') ||
      error.message?.includes('token') ||
      error.message?.includes('unauthorized')) {
    return ErrorTypes.AUTH;
  }
  
  // Server errors
  if (error.response?.status >= 500) {
    return ErrorTypes.SERVER;
  }
  
  // Validation errors (400 series)
  if (error.response?.status === 400 || 
      error.response?.status === 422) {
    return ErrorTypes.VALIDATION;
  }
  
  // AI-specific errors
  if (error.message?.includes('AI') || 
      error.message?.includes('copilot') ||
      (healthStatus && !healthStatus.ai.enabled)) {
    return ErrorTypes.AI_UNAVAILABLE;
  }
  
  return ErrorTypes.UNKNOWN;
};

const getErrorMessage = (error, type, healthStatus = null) => {
  // Use user-friendly message if provided
  if (error.userMessage) return error.userMessage;
  
  // Try to extract from response
  if (error.response?.data?.detail) return error.response.data.detail;
  if (error.response?.data?.message) return error.response.data.message;
  
  // Fallback to message or generic
  if (error.message) return error.message;
  
  switch (type) {
    case ErrorTypes.NETWORK: return 'Network connection failed';
    case ErrorTypes.BACKEND_DOWN: 
      return healthStatus?.backend.lastCheck 
        ? `Backend unavailable since ${new Date(healthStatus.backend.lastCheck).toLocaleTimeString()}`
        : 'Backend service is down';
    case ErrorTypes.CREDITS: return 'API credits exhausted';
    case ErrorTypes.RATE_LIMIT: return 'Rate limit exceeded';
    case ErrorTypes.SERVER: return 'Server error occurred';
    case ErrorTypes.AUTH: return 'Authentication required';
    case ErrorTypes.VALIDATION: return 'Validation error';
    case ErrorTypes.AI_UNAVAILABLE: 
      return healthStatus?.ai.enabled === false 
        ? 'AI features are currently disabled'
        : 'AI service unavailable';
    default: return 'An error occurred';
  }
};

const getErrorSuggestion = (type, healthStatus = null) => {
  switch (type) {
    case ErrorTypes.NETWORK: 
    case ErrorTypes.BACKEND_DOWN:
      if (healthStatus?.network.offline) {
        return 'You appear to be offline. Check your internet connection.';
      }
      return 'The service may be temporarily unavailable. Try again in a few moments.';
    case ErrorTypes.CREDITS: return 'Add credits at platform.deepseek.com to continue.';
    case ErrorTypes.RATE_LIMIT: return 'Please wait a few minutes before trying again.';
    case ErrorTypes.SERVER: return 'The service may be temporarily unavailable.';
    case ErrorTypes.AUTH: return 'Please log in again to continue.';
    case ErrorTypes.AI_UNAVAILABLE:
      if (healthStatus?.ai.configured === false) {
        return 'AI features are not configured. Contact support for assistance.';
      }
      return 'AI service may be restarting. Try again shortly.';
    default: return null;
  }
};

// Custom hook for components to use ErrorContext with HealthContext
const useErrorContextValue = () => {
  const [state, dispatch] = useReducer(errorReducer, { errors: [] });
  const healthStatus = useHealth(); // Get current health status

  const addError = useCallback((error, context = {}) => {
    const type = categorizeError(error, healthStatus);
    
    const errorObj = {
      type,
      message: getErrorMessage(error, type, healthStatus),
      suggestion: getErrorSuggestion(type, healthStatus),
      originalError: error,
      component: context.component || 'unknown',
      cid: context.cid,
      healthStatus, // Include health snapshot
      ...context
    };
    
    dispatch({ type: 'ADD_ERROR', payload: errorObj });
    
    // Auto-dismiss logic based on error type and health
    const autoDismiss = () => {
      // Don't auto-dismiss critical errors
      const criticalTypes = [
        ErrorTypes.NETWORK, 
        ErrorTypes.CREDITS, 
        ErrorTypes.AUTH,
        ErrorTypes.BACKEND_DOWN,
        ErrorTypes.AI_UNAVAILABLE
      ];
      
      if (!criticalTypes.includes(type)) {
        setTimeout(() => {
          dispatch({ type: 'REMOVE_ERROR', payload: { id: errorObj.id } });
        }, 8000);
      }
      
      // Special case: if network error but health says we're back online, auto-dismiss
      if (type === ErrorTypes.NETWORK && healthStatus?.network.online) {
        setTimeout(() => {
          dispatch({ type: 'REMOVE_ERROR', payload: { id: errorObj.id } });
        }, 3000);
      }
    };
    
    autoDismiss();
    
    return errorObj.id; // Return error ID for potential cleanup
  }, [healthStatus]);

  const removeError = useCallback((id) => {
    dispatch({ type: 'REMOVE_ERROR', payload: { id } });
  }, []);

  const clearErrors = useCallback(() => {
    dispatch({ type: 'CLEAR_ERRORS' });
  }, []);
  
  const clearErrorType = useCallback((errorType) => {
    dispatch({ type: 'CLEAR_TYPE', payload: { errorType } });
  }, []);

  // Clear errors when health status improves
  React.useEffect(() => {
    if (healthStatus?.backend.healthy) {
      const backendErrors = state.errors.filter(e => 
        [ErrorTypes.BACKEND_DOWN, ErrorTypes.SERVER].includes(e.type)
      );
      backendErrors.forEach(error => removeError(error.id));
    }
    
    if (healthStatus?.ai.enabled) {
      const aiErrors = state.errors.filter(e => 
        e.type === ErrorTypes.AI_UNAVAILABLE
      );
      aiErrors.forEach(error => removeError(error.id));
    }
    
    if (healthStatus?.network.online) {
      const networkErrors = state.errors.filter(e => 
        [ErrorTypes.NETWORK].includes(e.type)
      );
      networkErrors.forEach(error => removeError(error.id));
    }
  }, [healthStatus, state.errors, removeError]);

  return {
    errors: state.errors,
    addError,
    removeError,
    clearErrors,
    clearErrorType,
    hasErrors: state.errors.length > 0,
    hasCriticalError: state.errors.some(e => 
      [ErrorTypes.NETWORK, ErrorTypes.CREDITS, ErrorTypes.AUTH, 
       ErrorTypes.BACKEND_DOWN, ErrorTypes.AI_UNAVAILABLE].includes(e.type)
    ),
    getLatestError: () => state.errors[0],
    getErrorsByType: (type) => state.errors.filter(e => e.type === type),
    getErrorsByComponent: (component) => state.errors.filter(e => e.component === component),
    healthStatus // Expose health status for components
  };
};

export const ErrorProvider = ({ children }) => {
  // ErrorProvider must be inside HealthProvider to use useHealth
  const errorContextValue = useErrorContextValue();
  
  return (
    <ErrorContext.Provider value={errorContextValue}>
      {children}
    </ErrorContext.Provider>
  );
};

ErrorProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within ErrorProvider');
  }
  return context;
};

// Enhanced helper hook with health awareness
export const useErrorHandler = (componentName) => {
  const { addError, removeError, getErrorsByComponent, healthStatus } = useError();
  
  const handleError = useCallback((error, context = {}) => {
    const errorId = addError(error, { ...context, component: componentName });
    return errorId; // Return ID for potential cleanup
  }, [addError, componentName]);
  
  const clearComponentErrors = useCallback(() => {
    const componentErrors = getErrorsByComponent(componentName);
    componentErrors.forEach(error => removeError(error.id));
  }, [componentName, getErrorsByComponent, removeError]);
  
  const canRetry = useCallback((errorType) => {
    // Determine if retry makes sense based on error type and health
    switch (errorType) {
      case ErrorTypes.NETWORK:
        return healthStatus?.network.online;
      case ErrorTypes.BACKEND_DOWN:
        return healthStatus?.backend.healthy;
      case ErrorTypes.AI_UNAVAILABLE:
        return healthStatus?.ai.enabled;
      default:
        return true;
    }
  }, [healthStatus]);
  
  return {
    handleError,
    clearComponentErrors,
    componentErrors: getErrorsByComponent(componentName),
    healthStatus,
    canRetry
  };
};